import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { Select, Spin, Tooltip } from 'antd';
import { CaseListItem, TextVisitServiceType } from 'types/textVisit';
import useGetAllCase from 'hooks/textVisit/useGetAllCase';
import useMedicationSetting from 'hooks/useMedicationSetting';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import s from './s.module.less';
import { getTimeAgo, getDetailText, getRefillServiceTitle } from 'pages/dashboard/TextVisitPage/utils';
import { EnvironmentOutlined, ClockCircleOutlined } from '@ant-design/icons';
import KlarityEmpty from 'assets/common/klarityEmpty.svg';
import dayjs from 'dayjs';
import DefaultIcon from 'assets/conditionIcon/default-condition.png';
import { getAllCaseListForIneligible } from 'api/textVisit';

export const renderEmptyStateForMyCase = () => (
    <div className={s.emptyState}>
        <div className={s.emptyIcon}>
            <img src={KlarityEmpty} alt="empty" />
        </div>
        <h3 className={s.emptyTitle}>No cases found</h3>
    </div>
);

// 只在文本超过指定行数时显示 Tooltip 的组件
const EllipsisTooltip: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
    const textRef = useRef<HTMLParagraphElement>(null);
    const [isOverflow, setIsOverflow] = useState(false);

    useEffect(() => {
        const checkOverflow = () => {
            if (textRef.current) {
                // scrollHeight 大于 clientHeight 表示内容被截断
                setIsOverflow(textRef.current.scrollHeight > textRef.current.clientHeight);
            }
        };

        checkOverflow();
        // 监听窗口大小变化
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [text]);

    const content = <p ref={textRef} className={className}>{text}</p>;

    return isOverflow ? (
        <Tooltip title={text} placement="topLeft">
            {content}
        </Tooltip>
    ) : content;
};

interface AllCasePanelProps {
    onViewCase: (caseData: CaseListItem) => void;
    onRequestAccess?: (caseData: CaseListItem) => void;
    providerEligible?: boolean;
}

const AllCasePanel = forwardRef((props: AllCasePanelProps, ref) => {
    const { onViewCase, onRequestAccess, providerEligible } = props;
    const [selectedStates, setSelectedStates] = useState<string[]>([]);
    const [selectedGender, setSelectedGender] = useState<string[]>([]);
    const [selectedAge, setSelectedAge] = useState<string[]>([]);
    const [sortBy, setSortBy] = useState<string>('NEWEST');
    const [data] = useMedicationSetting();
    const [settings] = useSettings();
    const stateList = settings[EnumFields.STATE] || [];
    const [ineligibleCaseList, setIneligibleCaseList] = useState<CaseListItem[]>([]);
    const [ineligibleLoading, setIneligibleLoading] = useState(false);

    const states = data?.stateConditions ? Object.keys(data.stateConditions).map((item) => stateList.find((state) => state.value === item)!) : [];

    const [caseList, loading, fetchData] = useGetAllCase({
        filter: {
            states: selectedStates,
            gender: selectedGender,
            patientAge: selectedAge,
            sortBy,
            size: 500,
            current: 1,
        },
    });

    useEffect(() => {
        fetchData({
            states: selectedStates,
            gender: selectedGender,
            patientAge: selectedAge,
            sortBy,
            size: 500,
            current: 1,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStates, selectedGender, selectedAge, sortBy]);

    // 当 providerEligible 为 false 时，获取一次 mock 接口数据
    useEffect(() => {
        if (providerEligible === false) {
            const fetchIneligibleData = async () => {
                try {
                    setIneligibleLoading(true);
                    const result = await getAllCaseListForIneligible();
                    if (!result.error) {
                        const ineligibleData: CaseListItem[] = result?.data?.data || [];
                        setIneligibleCaseList(ineligibleData);
                    } else {
                        setIneligibleCaseList([]);
                    }
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to fetch ineligible case list:', e);
                    setIneligibleCaseList([]);
                } finally {
                    setIneligibleLoading(false);
                }
            };
            fetchIneligibleData();
        } else {
            // providerEligible 为 true 时，清空 mock 数据
            setIneligibleCaseList([]);
            setIneligibleLoading(false);
        }
    }, [providerEligible]);

    useImperativeHandle(ref, () => ({
        fetchData: () => {
            fetchData({
                states: selectedStates,
                gender: selectedGender,
                patientAge: selectedAge,
                sortBy,
                size: 500,
                current: 1,
            });
        },
    }));

    const renderCaseCard = (caseData: CaseListItem, isFromMockApi: boolean) => {
        const handleCardClick = () => {
            if (isFromMockApi && onRequestAccess) {
                // Data from the mock API, directly go through the access request flow
                onRequestAccess(caseData);
            } else {
                // Normal flow, view details
                onViewCase(caseData);
            }
        };

        const refillTitle = getRefillServiceTitle({
            serviceType: caseData.serviceType,
            specialty: (caseData as any).specialty ?? null,
            assessmentName: caseData.assessmentName,
        });

        return (
            <div
                key={caseData.id}
                className={s.caseCard}
                onClick={handleCardClick}
            >
                <div className={s.cardContent}>
                    <div className={s.caseCardHeader}>
                        <div className={s.caseIcon}>
                            <span className={caseData.icon ? s.caseIconImage : undefined}>
                                <img
                                    src={caseData.icon || DefaultIcon}
                                    alt={caseData.conditions}
                                    width={24}
                                    height={24}
                                />
                            </span>
                            <span>{refillTitle || caseData.assessmentName}</span>
                        </div>
                    </div>

                    <EllipsisTooltip text={caseData.aiSummary} className={s.caseDescription} />

                    <div className={s.caseLocation}>
                        <EnvironmentOutlined />
                        <span>{getDetailText(caseData, stateList)}</span>
                    </div>
                </div>

                <div className={s.caseFooter}>
                    <div className={s.caseTime}>
                        <ClockCircleOutlined />
                        <span>Submitted on {dayjs(caseData.patientSubmitAt).format('MMM D')}</span>
                    </div>
                    <div className={s.timeAgo} style={{ backgroundColor: getTimeAgo(caseData.patientSubmitAt || '').color }}>
                        <ClockCircleOutlined />
                        <span className={s.timeAgoText}>
                            {getTimeAgo(caseData.patientSubmitAt || '').text}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const renderEmptyState = () => {
        if (!providerEligible && ineligibleCaseList.length > 0) {
            return (
                <div className={s.casesGrid}>
                    {ineligibleCaseList.map((caseData) =>
                        renderCaseCard(caseData, true),
                    )}
                </div>
            );
        } else {
            return (
                <div className={s.emptyState}>
                    <div className={s.emptyIcon}>
                        <img src={KlarityEmpty} alt="empty" />
                    </div>
                    <h3 className={s.emptyTitle}>
                        No available text visit cases
                    </h3>
                    <p className={s.emptyDescription}>
                        All cases in your licensed states have been picked up by
                        other providers.
                    </p>
                    <p className={s.emptyDescription}>
                        We&apos;ll notify you by email when new cases become
                        available. To opt out, go to &quot;Settings&quot; &gt;
                        &quot;Email notifications&quot;.
                    </p>
                </div>
            );
        }
    };

    return (
        <div>
            <div className={s.filters}>
                <div className="flex gap-4">
                    {states.length > 0 && (
                        <div className={s.filterGroup}>
                            <label>States</label>
                            <Select
                                value={selectedStates}
                                onChange={setSelectedStates}
                                className={s.filterSelect}
                                mode="multiple"
                                placeholder="All"
                                dropdownStyle={{ minWidth: '160px' }}
                            >
                                {states.map((state) => (
                                    <Select.Option
                                        key={state.value}
                                        value={state.value}
                                    >
                                        {state.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    <div className={s.filterGroup}>
                        <label>Patient gender</label>
                        <Select
                            value={selectedGender}
                            onChange={setSelectedGender}
                            className={s.filterSelect}
                            mode="multiple"
                            placeholder="All"
                        >
                            <Select.Option value="Female">Female</Select.Option>
                            <Select.Option value="Male">Male</Select.Option>
                            <Select.Option value="Other">Other</Select.Option>
                        </Select>
                    </div>

                    <div className={s.filterGroup}>
                        <label>Patient age</label>
                        <Select
                            value={selectedAge}
                            className={s.filterSelect}
                            mode="multiple"
                            onChange={setSelectedAge}
                            placeholder="All"
                            dropdownStyle={{ minWidth: '160px' }}
                        >
                            <Select.Option value="PEDIATRIC">
                                Pediatric (0–17)
                            </Select.Option>
                            <Select.Option value="ADULT">
                                Adult (18–64)
                            </Select.Option>
                            <Select.Option value="SENIOR">
                                Senior (65+)
                            </Select.Option>
                        </Select>
                    </div>
                </div>

                <div className={s.filterGroup}>
                    <label>Sort by</label>
                    <Select
                        value={sortBy}
                        onChange={setSortBy}
                        className={s.filterSelect}
                    >
                        <Select.Option value="OLDEST">Oldest</Select.Option>
                        <Select.Option value="NEWEST">Newest</Select.Option>
                    </Select>
                </div>
            </div>

            <Spin spinning={loading}>
                {caseList.length > 0 ? (
                    <div className={s.casesGrid}>
                        {caseList.map((caseData) =>
                            renderCaseCard(caseData, false),
                        )}
                    </div>
                ) : (
                    renderEmptyState()
                )}
            </Spin>
        </div>
    );
});

export default AllCasePanel;
